/**
 * Pestaña "Pagos" — historial de abonos de una reserva.
 *
 * OJO con de dónde salen los datos: el pago inicial de una reserva simple NO
 * vive en `splitPayments`, sino en los campos de nivel superior de la reserva
 * (paymentMethod, paymentCurrency, advanceAmount...). `addPayment` lo migra a
 * `splitPayments[0]` la primera vez que se registra un abono adicional.
 *
 * Por eso reproducimos aquí el mismo criterio que usa el backend: si ya hay
 * splitPayments, esos son la verdad completa; si no los hay, sintetizamos el
 * pago inicial desde los campos top-level. Mezclarlos duplicaría el adelanto.
 */

import PropTypes from 'prop-types';
import {
  Box,
  Chip,
  Divider,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import PaymentsIcon from '@mui/icons-material/Payments';
import moment from 'moment';

const METHOD_LABELS = {
  cash: 'Efectivo',
  card: 'Tarjeta',
  transfer: 'Transferencia',
  zelle: 'Zelle',
  other: 'Otro',
  split: 'Dividido'
};

const ALLOCATION_LABELS = {
  room: 'Habitación',
  restaurant: 'Restaurante',
  sego: 'Sego'
};

const money = (value) => Number(value || 0).toFixed(2);

// Ver la nota del encabezado: splitPayments y el pago top-level son la misma
// plata, nunca se suman.
const buildPaymentList = (reservation) => {
  const split = reservation?.splitPayments || [];

  if (split.length > 0) {
    return [...split].sort((a, b) => new Date(a.addedAt || 0) - new Date(b.addedAt || 0));
  }

  if (Number(reservation?.advanceAmount) > 0) {
    return [
      {
        _id: 'initial',
        paymentMethod: reservation.paymentMethod,
        amount: reservation.advanceAmount,
        currency: reservation.paymentCurrency || 'USD',
        exchangeRateUsed: reservation.exchangeRateUsed || 1,
        amountInForeignCurrency: reservation.amountInForeignCurrency || 0,
        batchNumber: reservation.batchNumber,
        transactionNumber: reservation.transactionNumber,
        addedAt: reservation.createdDate,
        allocation: { room: reservation.advanceAmount, restaurant: 0, sego: 0 },
        isInitial: true
      }
    ];
  }

  return [];
};

const ReservationPayments = ({ reservation, currency, pendingBalance }) => {
  const payments = buildPaymentList(reservation);
  const totalPaid = payments.reduce((sum, p) => sum + Number(p.amount || 0), 0);

  if (payments.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 5, color: 'text.secondary' }}>
        <PaymentsIcon sx={{ fontSize: 44, opacity: 0.35, mb: 1 }} />
        <Typography variant="body2">Esta reserva todavía no tiene pagos registrados.</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        <Grid item xs={12} sm={6}>
          <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 1.5 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              Total abonado
            </Typography>
            <Typography variant="h6" fontWeight={700} color="success.main">
              {currency} {money(totalPaid)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {payments.length} {payments.length === 1 ? 'pago' : 'pagos'}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 1.5 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              Saldo pendiente
            </Typography>
            <Typography variant="h6" fontWeight={700} color={pendingBalance > 0 ? 'error.main' : 'text.primary'}>
              {currency} {money(pendingBalance)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Habitación + restaurante + Sego
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Fecha</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Método</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Moneda</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Referencia</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Aplicado a</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
                Monto
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {payments.map((payment, index) => {
              const isForeign = payment.currency && payment.currency !== 'USD';
              const allocations = Object.entries(payment.allocation || {}).filter(([, v]) => Number(v) > 0);

              return (
                <TableRow key={payment._id || index} hover>
                  <TableCell sx={{ fontSize: '0.8rem' }}>
                    {payment.addedAt ? moment(payment.addedAt).format('DD MMM YYYY, HH:mm') : '—'}
                    {payment.isInitial && <Chip label="Inicial" size="small" sx={{ ml: 0.75, height: 18, fontSize: '0.62rem' }} />}
                  </TableCell>

                  <TableCell sx={{ fontSize: '0.8rem' }}>{METHOD_LABELS[payment.paymentMethod] || payment.paymentMethod || '—'}</TableCell>

                  <TableCell sx={{ fontSize: '0.8rem' }}>
                    {isForeign ? (
                      <Stack spacing={0.25}>
                        <Chip
                          label={payment.currency}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ height: 20, fontSize: '0.68rem', width: 'fit-content' }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {payment.currency} {money(payment.amountInForeignCurrency)} @ {payment.exchangeRateUsed}
                        </Typography>
                      </Stack>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        USD
                      </Typography>
                    )}
                  </TableCell>

                  <TableCell sx={{ fontSize: '0.8rem' }}>
                    {payment.batchNumber || payment.transactionNumber ? (
                      <Stack spacing={0.25}>
                        {payment.batchNumber && (
                          <Typography variant="caption" color="text.secondary">
                            Lote: {payment.batchNumber}
                          </Typography>
                        )}
                        {payment.transactionNumber && (
                          <Typography variant="caption" color="text.secondary">
                            Ref: {payment.transactionNumber}
                          </Typography>
                        )}
                      </Stack>
                    ) : (
                      <Typography variant="caption" color="text.disabled">
                        —
                      </Typography>
                    )}
                  </TableCell>

                  <TableCell sx={{ fontSize: '0.8rem' }}>
                    {allocations.length > 0 ? (
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {allocations.map(([key, value]) => (
                          <Chip
                            key={key}
                            label={`${ALLOCATION_LABELS[key] || key} ${money(value)}`}
                            size="small"
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.65rem' }}
                          />
                        ))}
                      </Stack>
                    ) : (
                      <Typography variant="caption" color="text.disabled">
                        —
                      </Typography>
                    )}
                  </TableCell>

                  <TableCell align="right" sx={{ fontSize: '0.85rem', fontWeight: 700 }}>
                    {currency} {money(payment.amount)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Divider sx={{ my: 2 }} />

      <Typography variant="caption" color="text.secondary">
        Los montos se registran en USD como moneda base. Cuando el pago se recibe en otra moneda se guarda además el monto en esa divisa y
        la tasa aplicada en ese momento.
      </Typography>
    </Box>
  );
};

ReservationPayments.propTypes = {
  reservation: PropTypes.object,
  currency: PropTypes.string,
  pendingBalance: PropTypes.number
};

ReservationPayments.defaultProps = {
  reservation: null,
  currency: 'USD',
  pendingBalance: 0
};

export default ReservationPayments;
